import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import BaseModal from '../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { adjustInventoryItem } from '../../../../repository/InventoryDAO';

const AdjustInventoryModal = ({ show, onHide, item }) => {
    const [quantity, setQuantity] = useState(item?.quantity || 0); // Initialize with current quantity
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { showToast } = useToast();
    const { t } = useTranslation();

    const reasons = [
        { value: 'employee_use', label: t(StringKeys.EMPLOYEE_USE) },
        { value: 'customer_order', label: t(StringKeys.CUSTOMER_ORDER) },
        { value: 'damaged_goods', label: t(StringKeys.DAMAGED_GOODS) },
        { value: 'manual_adjustment', label: t(StringKeys.MANUAL_ADJUSTMENT) },
        { value: 'other', label: t(StringKeys.OTHER) }
    ];

    useEffect(() => {
        if (show && item) {
            setQuantity(item.quantity);
            resetForm();
        }
    }, [show, item]);

    const handleSubmit = async () => {
        setLoadingData(true);
        const adjustmentAmount = parseFloat(quantity) - item.quantity;
        const adjustmentType = adjustmentAmount > 0 ? "increase" : "decrease";

        try {
            await adjustInventoryItem(
                firebaseUser.organization,
                item.id,
                Math.abs(adjustmentAmount),
                adjustmentType,
                reason,
                notes,
                firebaseUser.uid,
                showToast
            );
            resetForm();
            onHide();
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        setReason("");
        setNotes("");
    };

    const handleQuantityChange = (e) => {
        const inputQuantity = parseInt(e.target.value, 10);
        setQuantity(inputQuantity >= 0 ? inputQuantity : 0); // Ensure non-negative values
    };

    const actions = (
        <Button onClick={handleSubmit} disabled={quantity < 0 || reason === ""}>
            {t(StringKeys.SUBMIT)}
        </Button>
    );

    if (!item) return null;

    return (
        <BaseModal
            open={show}
            onClose={onHide}
            title={t(StringKeys.ADJUST_INVENTORY)}
            actions={actions}
        >
            <TextField
                autoFocus
                margin="dense"
                id="quantity"
                label={`${t(StringKeys.QUANTITY)} (Current: ${item.quantity})`}
                type="number"
                fullWidth
                variant="outlined"
                value={quantity}
                onChange={handleQuantityChange}
                InputProps={{ inputProps: { min: 0 } }} // Ensure no negative values
            />
            <TextField
                select
                margin="dense"
                id="reason"
                label={t(StringKeys.REASON_FOR_ADJUSTMENT)}
                fullWidth
                variant="outlined"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
            >
                {reasons.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                margin="dense"
                id="notes"
                label={t(StringKeys.NOTES)}
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
            />
        </BaseModal>
    );
};

export default AdjustInventoryModal;
